import { useEffect, useMemo, useState } from 'react';
import { authApi, type CurrentSessionResult, type SessionSummaryResult } from '../auth/api';
import '../styles/modules-my-account-session.css';

interface MyCurrentSessionProps {
  readonly onReauthenticate?: () => void;
}

function formatDate(value: string): string {
  return new Intl.DateTimeFormat('fr-FR', {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(new Date(value));
}

function sortSessions(
  sessions: readonly SessionSummaryResult[],
  currentSessionId: string,
): readonly SessionSummaryResult[] {
  return [...sessions].sort((left, right) => {
    if (left.id === currentSessionId) return -1;
    if (right.id === currentSessionId) return 1;
    return new Date(right.lastSeenAt).getTime() - new Date(left.lastSeenAt).getTime();
  });
}

export function MyCurrentSession({
  onReauthenticate = () => window.location.replace('/'),
}: MyCurrentSessionProps = {}): React.JSX.Element {
  const [current, setCurrent] = useState<CurrentSessionResult | null>(null);
  const [sessions, setSessions] = useState<readonly SessionSummaryResult[]>([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [pendingSessionId, setPendingSessionId] = useState<string | null>(null);
  const [revokeAllPending, setRevokeAllPending] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;

    void Promise.all([authApi.currentSession(), authApi.listSessions()])
      .then(([currentResult, sessionResults]) => {
        if (!active) return;
        setCurrent(currentResult);
        setSessions(sessionResults);
      })
      .catch(() => {
        if (!active) return;
        setFailed(true);
      })
      .finally(() => {
        if (!active) return;
        setLoading(false);
      });

    return () => {
      active = false;
    };
  }, []);

  const orderedSessions = useMemo(
    () => (current === null ? sessions : sortSessions(sessions, current.sessionId)),
    [current, sessions],
  );

  async function revokeSession(session: SessionSummaryResult): Promise<void> {
    if (current === null || pendingSessionId !== null || revokeAllPending) return;

    const isCurrent = session.id === current.sessionId;
    const confirmed = window.confirm(
      isCurrent
        ? 'Déconnecter cette session ? Vous devrez vous reconnecter à Tosho.'
        : 'Déconnecter cette autre session ? Elle ne pourra plus accéder à Tosho.',
    );
    if (!confirmed) return;

    setPendingSessionId(session.id);
    setActionError(null);
    try {
      const { csrfToken } = await authApi.csrfToken();
      await authApi.revokeSession(session.id, csrfToken);
      if (isCurrent) {
        onReauthenticate();
        return;
      }
      setSessions((currentSessions) =>
        currentSessions.filter((candidate) => candidate.id !== session.id),
      );
    } catch {
      setActionError('Cette session ne peut pas être déconnectée pour le moment.');
    } finally {
      setPendingSessionId(null);
    }
  }

  async function revokeAllSessions(): Promise<void> {
    if (current === null || pendingSessionId !== null || revokeAllPending) return;

    const confirmed = window.confirm(
      'Déconnecter toutes vos sessions ? Vous devrez vous reconnecter à Tosho sur chaque appareil.',
    );
    if (!confirmed) return;

    setRevokeAllPending(true);
    setActionError(null);
    try {
      const { csrfToken } = await authApi.csrfToken();
      await authApi.revokeAllSessions(csrfToken);
      onReauthenticate();
    } catch {
      setActionError('Vos sessions ne peuvent pas être déconnectées pour le moment.');
      setRevokeAllPending(false);
    }
  }

  if (loading) {
    return <p className="my-account-session-state">Chargement de vos sessions…</p>;
  }

  if (failed || current === null) {
    return <p className="my-account-session-state">Vos sessions ne peuvent pas être affichées.</p>;
  }

  if (orderedSessions.length === 0) {
    return <p className="my-account-session-state">Aucune session active ne peut être affichée.</p>;
  }

  return (
    <div className="my-account-sessions">
      <div className="my-account-sessions__toolbar">
        <p>
          {orderedSessions.length === 1
            ? '1 session active'
            : `${orderedSessions.length} sessions actives`}
        </p>
        <button
          type="button"
          className="my-account-session-revoke-all"
          disabled={pendingSessionId !== null || revokeAllPending}
          onClick={() => void revokeAllSessions()}
        >
          {revokeAllPending ? 'Déconnexion…' : 'Déconnecter toutes mes sessions'}
        </button>
      </div>

      {actionError ? (
        <p className="my-account-session-error tosho-feedback is-error" role="alert">
          {actionError}
        </p>
      ) : null}

      <ol className="my-account-session-list">
        {orderedSessions.map((session) => {
          const isCurrent = session.id === current.sessionId;
          const pending = pendingSessionId === session.id;
          return (
            <li key={session.id} className={isCurrent ? 'is-current' : undefined}>
              <div className="my-account-session-card__heading">
                <div>
                  <strong>{isCurrent ? 'Session actuelle' : 'Autre session'}</strong>
                  {isCurrent ? <span>Vous êtes ici</span> : null}
                </div>
                <button
                  type="button"
                  className="my-account-session-revoke"
                  disabled={pendingSessionId !== null || revokeAllPending}
                  onClick={() => void revokeSession(session)}
                >
                  {pending ? 'Déconnexion…' : 'Déconnecter'}
                </button>
              </div>
              <dl className="my-account-session-grid">
                <div>
                  <dt>Dernière activité</dt>
                  <dd>{formatDate(session.lastSeenAt)}</dd>
                </div>
                <div>
                  <dt>Connexion</dt>
                  <dd>{formatDate(session.authenticatedAt)}</dd>
                </div>
                <div>
                  <dt>Expiration d’inactivité</dt>
                  <dd>{formatDate(session.idleExpiresAt)}</dd>
                </div>
                <div>
                  <dt>Expiration maximale</dt>
                  <dd>{formatDate(session.absoluteExpiresAt)}</dd>
                </div>
              </dl>
            </li>
          );
        })}
      </ol>
    </div>
  );
}
